/**
 * Arrival at an appointment, in near real time, for the in-visit recording
 * prompt ("Looks like you're at today's appointment with Dr. Felix").
 *
 * The background location task reports a stopped phone late or not at all
 * (200 m distance filter, 15 min batching, updates pause), so on its own the
 * first sign of a visit is the departure. Geofencing is already in the shipped
 * binary (expo-location's geofencing task consumer, with expo-task-manager), so
 * this registers today's appointments that have a place as regions, and on an
 * ENTER posts one ordinary point to /location/sync for that plan. The server
 * matches the point to the plan's own appointments (time window + place); the
 * region and its id are never taken as proof.
 *
 * Same gates as location sharing: the device opt-in and the Always grant.
 * iOS monitors at most 20 regions per app, so at most MAX_REGIONS go in.
 */
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { store } from '../store';
import { fetchAppointmentRegions, syncLocationData, type AppointmentRegion } from '../store/actions/location';
import { LOCATION_SHARING_OPT_IN_KEY } from '../constants/location';
import debug from './debug';

export const APPOINTMENT_GEOFENCE_TASK = 'ELEPLAN_APPOINTMENT_GEOFENCES';
const MAX_REGIONS = 18;
/** Regions are refreshed at most this often (and on every foreground after it). */
const REFRESH_MS = 30 * 60 * 1000;
const LAST_REFRESH_KEY = '@eleplan/appointment_geofences_refreshed_ms';
/** identifier → ends_at (ms) of the regions registered, so an ENTER after the visit is ignored. */
const REGION_ENDS_KEY = '@eleplan/appointment_geofences_ends';

let TaskManager: typeof import('expo-task-manager') | null = null;
let Location: typeof import('expo-location') | null = null;
if (Platform.OS !== 'web') {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    TaskManager = require('expo-task-manager') as typeof import('expo-task-manager');
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    Location = require('expo-location') as typeof import('expo-location');
  } catch {
    // Native modules unavailable in this runtime.
  }
}

/** `appt:<projectId>:<eventId>` → the plan the region belongs to. */
export function projectIdFromRegion(identifier: unknown): string | null {
  const m = /^appt:(\d+):/.exec(String(identifier || ''));
  return m ? m[1] : null;
}

function signedIn(): boolean {
  const state: any = store.getState();
  return state?.session?.authorized === true && !!state?.session?.account?.account_id && !!state?.authentication?.user?.cognito_id;
}

if (TaskManager && Location) {
  try {
    TaskManager.defineTask(APPOINTMENT_GEOFENCE_TASK, async ({ data, error }: any) => {
      try {
        if (error || !Location) return;
        if (data?.eventType !== Location.GeofencingEventType.Enter) return;
        if (await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY) !== 'true' || !signedIn()) return;
        const projectId = projectIdFromRegion(data?.region?.identifier);
        if (!projectId) return;
        // A region whose appointment is over is never acted on; the next
        // refresh drops it.
        const ends = JSON.parse((await AsyncStorage.getItem(REGION_ENDS_KEY)) || '{}');
        const endsAt = Number(ends?.[data.region.identifier]);
        if (!Number.isFinite(endsAt) || Date.now() > endsAt) return;
        // The phone's own fix, never the region's centre: the server must see
        // where the person is, not where we hoped they would be.
        const position = await Location.getCurrentPositionAsync({ accuracy: Location.Accuracy.Balanced })
          .catch(() => Location!.getLastKnownPositionAsync());
        const coords = position?.coords;
        if (!coords) return;
        await store.dispatch(syncLocationData(projectId, {
          latitude: coords.latitude,
          longitude: coords.longitude,
          accuracy: typeof coords.accuracy === 'number' ? coords.accuracy : null,
          altitude: typeof coords.altitude === 'number' ? coords.altitude : null,
          heading: typeof coords.heading === 'number' ? coords.heading : null,
          speed: typeof coords.speed === 'number' ? coords.speed : null,
          timestamp: new Date(position?.timestamp || Date.now()).toISOString(),
          source: Platform.OS === 'ios' ? 'ios-geofence-enter' : 'android-geofence-enter',
        }) as any);
        debug.log('location', 'Appointment geofence entered; point sent');
      } catch (err) {
        debug.warn('Appointment geofence task failed', err);
      }
    });
  } catch {
    // Already defined, or no task manager.
  }
}

export async function stopAppointmentGeofences(): Promise<void> {
  if (!Location) return;
  try {
    if (await Location.hasStartedGeofencingAsync(APPOINTMENT_GEOFENCE_TASK)) {
      await Location.stopGeofencingAsync(APPOINTMENT_GEOFENCE_TASK);
    }
    await AsyncStorage.multiRemove([LAST_REFRESH_KEY, REGION_ENDS_KEY]);
  } catch (err) {
    debug.warn('Failed to stop appointment geofences', err);
  }
}

/**
 * Register today's appointment regions for every plan this person is on.
 * A no-op without the opt-in and the Always grant, and at most every 30 min.
 */
export async function refreshAppointmentGeofences({ force = false }: { force?: boolean } = {}): Promise<void> {
  if (Platform.OS === 'web' || !Location || !TaskManager) return;
  try {
    if (await AsyncStorage.getItem(LOCATION_SHARING_OPT_IN_KEY) !== 'true' || !signedIn()) {
      await stopAppointmentGeofences();
      return;
    }
    const bg = await Location.getBackgroundPermissionsAsync();
    if (bg.status !== 'granted') return;
    const last = Number(await AsyncStorage.getItem(LAST_REFRESH_KEY)) || 0;
    // Refresh early when a registered appointment has ended, so its region goes.
    const ends = JSON.parse((await AsyncStorage.getItem(REGION_ENDS_KEY)) || '{}');
    const anEnded = Object.values(ends || {}).some((v) => Number(v) < Date.now());
    if (!force && !anEnded && Date.now() - last < REFRESH_MS) return;

    const projectIds = Object.keys((store.getState() as any)?.projects?.list ?? {});
    const regions: AppointmentRegion[] = [];
    for (const projectId of projectIds) {
      if (regions.length >= MAX_REGIONS) break;
      const res: any = await store.dispatch(fetchAppointmentRegions(projectId) as any);
      for (const region of res?.regions || []) {
        const endsAt = Date.parse(region.ends_at);
        if (regions.length < MAX_REGIONS && projectIdFromRegion(region.identifier) === String(projectId)
          && Number.isFinite(endsAt) && endsAt > Date.now()) regions.push(region);
      }
    }
    if (regions.length === 0) {
      await stopAppointmentGeofences();
    } else {
      await Location.startGeofencingAsync(APPOINTMENT_GEOFENCE_TASK, regions.map((r) => ({
        identifier: r.identifier,
        latitude: r.latitude,
        longitude: r.longitude,
        radius: r.radius,
        notifyOnEnter: true,
        notifyOnExit: false,
      })));
    }
    await AsyncStorage.setItem(REGION_ENDS_KEY, JSON.stringify(Object.fromEntries(regions.map((r) => [r.identifier, Date.parse(r.ends_at)]))));
    await AsyncStorage.setItem(LAST_REFRESH_KEY, String(Date.now()));
    debug.log('location', 'Appointment geofences refreshed', { regions: regions.length });
  } catch (err) {
    debug.warn('Failed to refresh appointment geofences', err);
  }
}
